import { gql, QueryResult } from '@apollo/client'
import { navigate, RouteComponentProps } from '@reach/router'
import * as React from 'react'
import styled, { css } from 'styled-components'
import Button from '../../../../common/src/components/Button'
import link from '../../../../common/src/components/Link'
import { EllipsisSpan, Spacer } from '../../../../common/src/components/core'
import { Colors, Fonts } from '../../../../common/src/constants'
import { handleError } from '../../../../common/src/data/errors'
import { TestIds } from '../../../../common/src/data/testids'
import title from '../../../../common/src/title'
import { CreateUserMutation, FetchAdminQuery, FetchUsersQuery, UserType } from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { Fragments } from '../../graphql/fragments'
import { PollingQuery } from '../components/PollingQuery'
import { Unauthorized } from '../components/Unauthorized'
import { AppRouteParams, getFirmPath } from '../route'
import AdminsDetail from './AdminsDetail'
import CreateFirmDetail from './CreateFirmDetail'
import FirmDetail from './FirmDetail'
import { FirmsDetail } from './FirmsDetail'
import OperatorsDetail from './OperatorsDetail'
import OtherUsersDetail from './OtherUsersDetail'
import SettingsDetail from './SettingsDetail'
import UserDetail from './UserDetail'

const fetchAdminQuery = gql`
  query FetchAdmin {
    allFirms {
      ...Firm
      numUnits
      timeCreated
    }
  }
  ${Fragments.firm}
`

const fetchUsersQuery = gql`
  query FetchUsers {
    allUsers {
      ...UserAdminDetails
    }
  }
  ${Fragments.userAdminDetails}
`

const createUserMutation = gql`
  mutation CreateUser($user: UserInput!) {
    createUser(user: $user) {
      id
    }
  }
`

export type TabType = 'ENGINEERING' | 'OPERATIONS' | 'SUPPORT' | 'OTHER_USERS' | 'FIRMS' | 'NEW_FIRM' | 'SETTINGS'

interface AdminPageProps extends RouteComponentProps, AppRouteParams {
  canViewAdminPage: boolean
  canViewFirmSecrets?: boolean
  tab: TabType
}

class AdminPageImpl extends React.PureComponent<AdminPageProps & QueryResult<FetchAdminQuery>> {
  componentDidMount() {
    title.set('Admin')
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  render() {
    const { tab, userId, firmId, data, canViewAdminPage, canViewFirmSecrets } = this.props

    if (!canViewAdminPage) {
      return (
        <AdminWrap>
          <Unauthorized />
        </AdminWrap>
      )
    }

    let body

    if (userId) {
      body = <UserDetail userId={Number(userId)} />
    } else if (tab === 'NEW_FIRM') {
      body = <CreateFirmDetail onCreate={this.onFinishCreateFirmBound} />
    } else if (firmId) {
      body = <FirmDetail firmId={Number(firmId)} canViewFirmSecrets={!!canViewFirmSecrets} />
    } else if (adminTabTypes.includes(tab)) {
      body = <AdminsDetail />
    } else if (tab === 'OPERATIONS') {
      body = <OperatorsDetail />
    } else if (tab === 'OTHER_USERS') {
      body = <OtherUsersDetail />
    } else if (tab === 'FIRMS') {
      body = <FirmsDetail firms={data?.allFirms} />
    } else if (tab === 'SETTINGS') {
      body = <SettingsDetail />
    }

    let sidebar

    if (adminTabTypes.includes(tab) || tab === 'OPERATIONS' || tab === 'OTHER_USERS') {
      sidebar = <UserSidebar tab={tab} firmId={1} userId={Number(userId)} onCreate={this.createUserBound} />
    } else if (tab === 'FIRMS') {
      sidebar = null
    } else {
      sidebar = <SubSidebarWrap />
    }

    return (
      <AdminWrap>
        <NavigationSidebar tab={tab} firmId={Number(firmId)} />
        {sidebar}
        <AdminBody data-testid={TestIds.adminBody}>{body}</AdminBody>
      </AdminWrap>
    )
  }

  private createUserBound = () => {
    const { tab } = this.props

    let userType
    if (tab === 'ENGINEERING') {
      userType = UserType.ENGINEER
    } else if (tab === 'OPERATIONS') {
      userType = UserType.OPERATOR
    } else if (tab === 'SUPPORT') {
      userType = UserType.SUPPORT
    } else if (tab === 'OTHER_USERS') {
      userType = UserType.SALES
    } else {
      return
    }

    const email = window.prompt("Enter new user's Appfolio email address: ")
    if (!email) {
      return
    }

    const user = {
      email,
      userType,
      name: 'New User',
    }

    apolloWebClient
      .mutate<CreateUserMutation>({
        mutation: createUserMutation,
        variables: { user },
      })
      .then(result => {
        const newUserId = result.data!.createUser.id

        // Refetch the user list for that firm, updating UsersSidebar
        apolloWebClient
          .query({
            query: fetchUsersQuery,
            fetchPolicy: 'network-only',
          })
          .then(() => navigate(getUserLink(tab, newUserId)))
          .catch(handleError)
      })
      .catch(handleError)
  }

  private onFinishCreateFirmBound = (firmId: number) => {
    navigate(getFirmPath(firmId)).catch(handleError)
  }
}

interface FirmSidebarProps {
  tab: TabType
  firmId?: number | null
}

function scrollToTop() {
  window.scrollTo(0, 0)
}

class NavigationSidebar extends React.PureComponent<FirmSidebarProps> {
  render() {
    const { tab } = this.props
    return (
      <SidebarWrap>
        <CreateLinkButton color="sky" to="/leasing/admin/firms/new" noTab>
          Add Firm
        </CreateLinkButton>
        <Spacer height={32} />
        <Item isSelected={tab === 'ENGINEERING'} to="/leasing/admin/">
          Engineering
        </Item>
        <Item isSelected={tab === 'OPERATIONS'} to="/leasing/admin/operations">
          Operations
        </Item>
        <Item isSelected={tab === 'SUPPORT'} to="/leasing/admin/support">
          Support
        </Item>
        <Item isSelected={tab === 'OTHER_USERS'} to="/leasing/admin/other">
          Other users
        </Item>
        <Spacer height={64} />
        <Item isSelected={tab === 'FIRMS'} to="/leasing/admin/firms">
          Firms
        </Item>
        <Item isSelected={tab === 'SETTINGS'} to="/leasing/admin/settings">
          Settings
        </Item>
      </SidebarWrap>
    )
  }
}

interface UserSidebarProps {
  tab: TabType
  firmId: number
  userId: number
  onCreate: () => void
}

class UserSidebarImpl extends React.PureComponent<UserSidebarProps & QueryResult<FetchUsersQuery>> {
  render() {
    const { data, tab, userId, onCreate } = this.props
    if (data == null) {
      return null
    }

    let users = data!.allUsers
    if (users == null) {
      return <SubSidebarWrap />
    }

    const opsUserTypes = new Set([
      'TRAINING',
      'OPERATOR',
      'SENIOR_OPERATOR',
      'TEAM_LEAD',
      'ANALYST',
      'OPS_MANAGER',
      UserType.CUSTOMER_STEWARD,
      UserType.CUSTOMER_STEWARD_TRAINING,
    ])
    const supportUserTypes = new Set([UserType.SUPPORT, UserType.SUPPORT_LIMITED])
    const otherUserTypes = new Set(['SALES', 'DISABLED'])

    if (tab === 'ENGINEERING') {
      users = users.filter(u => u.userType === UserType.ENGINEER)
    } else if (tab === 'OPERATIONS') {
      users = users.filter(u => opsUserTypes.has(u.userType))
    } else if (tab === 'SUPPORT') {
      users = users.filter(u => supportUserTypes.has(u.userType))
    } else if (tab === 'OTHER_USERS') {
      users = users.filter(u => otherUserTypes.has(u.userType))
    }

    const sortedUsers = users.slice().sort((a, b) => {
      const ua = a.userType === 'DISABLED' ? 1 : 0
      const ub = b.userType === 'DISABLED' ? 1 : 0
      if (ua !== ub) {
        return ua - ub
      } else if (a.name !== b.name) {
        return a.name.localeCompare(b.name)
      }
      return a.id - b.id
    })

    return (
      <SubSidebarWrap>
        <CreateButton onClick={onCreate}>Add User</CreateButton>
        <Spacer height={32} />
        {sortedUsers.map(u => (
          <SubItem key={u.id} isSelected={u.id === userId} to={getUserLink(tab, u.id)} onClick={scrollToTop}>
            <NameSpan isDisabled={u.userType === 'DISABLED'}>{u.name || u.email}</NameSpan>
          </SubItem>
        ))}
      </SubSidebarWrap>
    )
  }
}

function UserSidebar(props: UserSidebarProps) {
  return (
    <PollingQuery query={fetchUsersQuery}>
      {(res: QueryResult<FetchUsersQuery>) => <UserSidebarImpl {...props} {...res} />}
    </PollingQuery>
  )
}

function getUserLink(tab: TabType, userId: number) {
  let prefix
  if (tab === 'ENGINEERING') {
    prefix = '/leasing/admin/engineering/'
  } else if (tab === 'OPERATIONS') {
    prefix = '/leasing/admin/operations/'
  } else if (tab === 'SUPPORT') {
    prefix = '/leasing/admin/support/'
  } else if (tab === 'OTHER_USERS') {
    prefix = '/leasing/admin/other/'
  } else {
    throw new Error('Unexpected tab: ' + tab)
  }
  return prefix + userId
}

const AdminWrap = styled.div`
  min-width: 1280px;
  display: flex;
`

const SidebarWrap = styled.div`
  width: 256px;
  padding: 16px;
  overflow: hidden;
`

const SubSidebarWrap = styled.div`
  width: 194px;
  padding: 16px 0;
  overflow: hidden;
`

const CreateButton = styled(Button)`
  width: 100%;
`

const CreateLinkButton = link(CreateButton)

const LinkButton = styled.button`
  ${(props: { isSelected?: boolean }) => css`
    display: block;
    cursor: pointer;
    padding: 12px 24px 14px;
    margin: 0 0 24px auto;
    border-radius: 4px;
    border: solid 1px ${props.isSelected ? Colors.charcoal : 'transparent'};
    color: ${props.isSelected ? Colors.white : Colors.midway};
    background-color: ${props.isSelected ? Colors.black : 'inherit'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: 0.2s;

    &:hover {
      color: ${props.isSelected ? Colors.white : Colors.silver};
      border: solid 1px ${Colors.charcoal};
    }
  `};
`

const Item = link(styled(LinkButton)`
  font-size: 21px;
  font-family: ${Fonts.sansHeader};
  font-weight: normal;
`)

const SubItem = link(styled(LinkButton)`
  font-size: 16px;
  font-family: ${Fonts.sansHeader};
  font-weight: normal;
`)

const NameSpan = styled(EllipsisSpan)`
  ${(props: { isDisabled?: boolean }) => css`
    color: ${props.isDisabled ? Colors.midway : 'inherit'};
    text-decoration: ${props.isDisabled ? 'line-through' : 'none'};
  `};
`

const AdminBody = styled.div`
  padding: 16px;
  margin-left: 48px;
  width: 700px;
`

export default function AdminPage(props: AdminPageProps) {
  return (
    <PollingQuery query={fetchAdminQuery}>
      {(res: QueryResult<FetchAdminQuery>) => <AdminPageImpl {...props} {...res} />}
    </PollingQuery>
  )
}

export const adminTabTypes: TabType[] = ['ENGINEERING', 'SUPPORT']
